import React from 'react';

import { LANGUAGES, PION_TYPES } from '../../constants';
import withLang from '../../hoc/withLang';
import { CardType, PionType } from '../../type';
import ActionPion from '../ActionPion';
import CardPion from '../CardPion';
import PendingPion from '../PendingPion';
import Pion from '../Pion';
import Spinner from '../Spinner';
import TipPion from '../TipPion';

import CardContent from './CardContent';
import { LangContext } from './LangContext';

import './card.scss';

const BACKGROUND_FALLBACK_COLOR = '#6F7B89';

const getBackgroundColor = (card: CardType) => {
  if (!card || card.get('pions')) {
    return null;
  }

  if (
    card &&
    card.getIn(['image', 'averageColor']) &&
    card
      .get('pions')
      .filter((pion: PionType) => pion.get('type') === PION_TYPES.PHOTO).size
  ) {
    return card.getIn(['image', 'averageColor']);
  }

  return BACKGROUND_FALLBACK_COLOR;
};

export type CardPropsType = {
  card: CardType;
  pions: PionType[];
  headerImages: any[];
  logoUrl: string;
  isPublic?: boolean;
  isLoading?: boolean;
  hasCardPions?: boolean;
  lang: string;
  renderTags?: () => React.ReactNode;
  renderPions?: () => React.ReactNode;
  renderTipPions?: () => React.ReactNode;
  renderCardPions?: () => React.ReactNode;
  renderActionPions?: () => React.ReactNode;
  renderPendingPions?: () => React.ReactNode;
  renderPionsNotInCard?: () => React.ReactNode;
  renderPionsWithHomerBadge?: () => React.ReactNode;
  renderPionsNotInCardWithOneHome?: () => React.ReactNode;
};

const Card = ({
  card,
  lang = LANGUAGES.EN,
  isLoading = false,
  hasCardPions = false,
  renderPions,
  renderCardPions,
  renderActionPions,
  renderPendingPions,
  renderTipPions,
  renderTags,
  renderPionsNotInCard,
  renderPionsNotInCardWithOneHome,
  renderPionsWithHomerBadge,
}: CardPropsType) => {
  const backgroundColor = getBackgroundColor(card);

  return (
    <LangContext.Provider value={lang}>
      <div className='card' style={{ backgroundColor }}>
        <div className='card__header'>
          {card.get('title') && (
            <h1 className='card__title'>{card.get('title')}</h1>
          )}

          <div className='card__slider'>{isLoading && <Spinner />}</div>
        </div>

        {renderTags && (
          <div>
            <ul className='card__tags'>{renderTags()}</ul>
          </div>
        )}

        <div className='card__content'>
          <CardContent
            hasCardPions={hasCardPions}
            renderPions={renderPions}
            renderCardPions={renderCardPions}
            renderActionPions={renderActionPions}
            renderPendingPions={renderPendingPions}
            renderTipPions={renderTipPions}
            renderPionsNotInCard={renderPionsNotInCard}
            renderPionsNotInCardWithOneHome={renderPionsNotInCardWithOneHome}
            renderPionsWithHomerBadge={renderPionsWithHomerBadge}
          />
        </div>
      </div>
    </LangContext.Provider>
  );
};

type ActionPionPropsType = {
  pion: PionType;
  onPionClick: () => void;
  onAccept: (pion: PionType) => void;
  onDecline: (pion: PionType) => void;
  lang: string;
};

Card.Pion = withLang(Pion);
Card.Pion.displayName = 'Card.Pion';

Card.ActionPion = withLang<ActionPionPropsType>(ActionPion);
Card.ActionPion.displayName = 'Card.ActionPion';

Card.PendingPion = withLang(PendingPion);
Card.PendingPion.displayName = 'Card.PendingPion';

Card.TipPion = withLang(TipPion);
Card.TipPion.displayName = 'Card.TipPion';

Card.CardPion = CardPion;
// Card.CardPion.displayName = 'Card.CardPion';

export default Card;
